// @ts-nocheck
// biome-ignore-all lint: copied from kimyoDUv2 template and adapted for this app.
import {
	ArrowLeft,
	Bell,
	ChevronDown,
	Download,
	KeyRound,
	LogOut,
	Menu,
	Moon,
	Plus,
	Search,
	Sun,
	User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLogout } from "@/modules/auth/hooks/useAuth";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { useTheme } from "../context/ThemeContext";
import { useNotificationsData } from "../hooks/useNotificationsData";
import type { SearchResult } from "../services/search.service";
import { getSearchResultHref, searchGlobal } from "../services/search.service";

const pageTitles: Record<string, string> = {
	"/": "Dashboard",
	"/monitoring": "Real-time Monitoring",
	"/journal": "Kirdi-Chiqdi Jurnali",
	"/students": "Talabalar",
	"/teachers": "O'qituvchilar",
	"/employees": "Xodimlar",
	"/attendance": "Davomat jadvali",
	"/buildings": "Binolar",
	"/devices": "FaceID Qurilmalar",
	"/reports": "Hisobotlar",
	"/settings/holidays": "Bayram kunlari",
	"/settings": "Sozlamalar",
};

const weekdays = ["Yakshanba", "Dushanba", "Seshanba", "Chorshanba", "Payshanba", "Juma", "Shanba"];

const notifIcon: Record<string, string> = {
	danger: "🔴",
	warning: "🟡",
	success: "🟢",
	info: "🔵",
};

function LiveClock() {
	const [time, setTime] = useState(new Date());

	useEffect(() => {
		const timer = setInterval(() => setTime(new Date()), 1000);
		return () => clearInterval(timer);
	}, []);

	const pad = (n: number) => String(n).padStart(2, "0");
	const timeStr = `${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`;
	const dateStr = `${time.getFullYear()}.${pad(time.getMonth() + 1)}.${pad(time.getDate())} (${weekdays[time.getDay()]})`;

	return (
		<>
			{/* Date */}
			<div
				className="app-topbar-date"
				style={{
					fontSize: 12,
					color: "var(--app-text-muted)",
					whiteSpace: "nowrap",
					flexShrink: 0,
				}}
			>
				{dateStr}
			</div>

			{/* Live Clock */}
			<div
				className="app-topbar-clock"
				style={{
					fontSize: 15,
					fontWeight: 600,
					color: "#00C98D",
					fontFamily: "'JetBrains Mono', monospace",
					letterSpacing: "0.05em",
					minWidth: 80,
					whiteSpace: "nowrap",
				}}
			>
				{timeStr}
			</div>
		</>
	);
}

export function Topbar({ onMenuClick }: { onMenuClick?: () => void }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { isDark, toggleTheme } = useTheme();
	const { data: notifications = [] } = useNotificationsData();
	const user = useAuthStore((state) => state.user);
	const { mutate: logout } = useLogout();
	const [showNotif, setShowNotif] = useState(false);
	const [showUser, setShowUser] = useState(false);
	const [notifList, setNotifList] = useState(notifications);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
	const [showSearch, setShowSearch] = useState(false);
	const [showExport, setShowExport] = useState(false);
	const [exportingType, setExportingType] = useState<string | null>(null);
	const [searchLoading, setSearchLoading] = useState(false);
	const [mobileSearchActive, setMobileSearchActive] = useState(false);

	useEffect(() => {
		if (notifications && notifications.length > 0) {
			setNotifList(notifications);
		}
	}, [notifications]);
	const searchRef = useRef<HTMLDivElement>(null);
	const notifRef = useRef<HTMLDivElement>(null);
	const userRef = useRef<HTMLDivElement>(null);
	const exportRef = useRef<HTMLDivElement>(null);

	const pageTitle =
		Object.entries(pageTitles).find(([key]) =>
			key === "/" ? location.pathname === "/" : location.pathname.startsWith(key)
		)?.[1] || "Dashboard";

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowSearch(false);
			if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotif(false);
			if (userRef.current && !userRef.current.contains(e.target as Node)) setShowUser(false);
			if (exportRef.current && !exportRef.current.contains(e.target as Node)) setShowExport(false);
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	useEffect(() => {
		const query = searchTerm.trim();
		if (query.length < 2) {
			setSearchResults([]);
			setSearchLoading(false);
			return;
		}

		let active = true;
		setSearchLoading(true);
		const timer = window.setTimeout(() => {
			searchGlobal(query)
				.then((results) => {
					if (active) {
						setSearchResults(results);
						setShowSearch(true);
					}
				})
				.catch(() => {
					if (active) {
						setSearchResults([]);
					}
				})
				.finally(() => {
					if (active) {
						setSearchLoading(false);
					}
				});
		}, 250);

		return () => {
			active = false;
			window.clearTimeout(timer);
		};
	}, [searchTerm]);

	const unread = notifList.filter((n) => !n.read).length;

	const markAllRead = () => setNotifList((prev) => prev.map((n) => ({ ...n, read: true })));

	const openSearchResult = (result: SearchResult) => {
		setSearchTerm("");
		setSearchResults([]);
		setShowSearch(false);
		navigate(getSearchResultHref(result));
	};

	const handleTopExport = async (
		type: "today_all" | "today_employee" | "students" | "teachers" | "employees"
	) => {
		if (exportingType) return;
		setExportingType(type);
		setShowExport(false);
		try {
			const { apiClient } = await import("../../../app/api/client");
			const now = new Date();
			const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
			let url = "";
			let params: Record<string, string> = { format: "excel" };
			let defaultName = "export.xlsx";

			if (type === "today_all") {
				url = "/reports/export";
				params = { startDate: today, endDate: today, format: "excel" };
				defaultName = `umumiy-davomat-${today}.xlsx`;
			} else if (type === "today_employee") {
				url = "/reports/export";
				params = { startDate: today, endDate: today, userType: "employee", format: "excel" };
				defaultName = `xodimlar-davomati-${today}.xlsx`;
			} else if (type === "students") {
				url = "/users/export";
				params = { role: "student", format: "excel" };
				defaultName = `talabalar-royxati-${today}.xlsx`;
			} else if (type === "teachers") {
				url = "/users/export";
				params = { role: "teacher", format: "excel" };
				defaultName = `oqituvchilar-royxati-${today}.xlsx`;
			} else if (type === "employees") {
				url = "/users/export";
				params = { role: "employee", format: "excel" };
				defaultName = `xodimlar-royxati-${today}.xlsx`;
			}

			const res = await apiClient.get<Blob>(url, {
				params,
				responseType: "blob",
			});
			const disposition = res.headers["content-disposition"] ?? "";
			const match =
				/filename\*=?UTF-8''([^";]+)/i.exec(disposition) ??
				/filename="?([^";]+)"?/i.exec(disposition);
			const filename = match?.[1] ? decodeURIComponent(match[1]) : defaultName;
			const blobUrl = URL.createObjectURL(res.data);
			const anchor = document.createElement("a");
			anchor.href = blobUrl;
			anchor.download = filename;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			URL.revokeObjectURL(blobUrl);
		} catch (err) {
			console.error("Export failed:", err);
		} finally {
			setExportingType(null);
		}
	};

	return (
		<header
			className="app-topbar"
			style={{
				height: "var(--app-topbar-height, 60px)",
				background: "var(--app-surface)",
				borderBottom: "1px solid var(--app-border)",
				display: "flex",
				alignItems: "center",
				padding: "var(--app-topbar-padding, 0 24px)",
				gap: "var(--app-topbar-gap, 12px)",
				position: "sticky",
				top: 0,
				zIndex: 40,
				fontFamily: "'Inter', sans-serif",
				transition: "background 0.2s ease, border-color 0.2s ease",
			}}
		>
			<button
				type="button"
				className="app-mobile-menu-trigger"
				aria-label="Menyuni ochish"
				onClick={onMenuClick}
			>
				<Menu size={20} />
			</button>

			{/* Page title */}
			<div
				className="app-topbar-title"
				style={{
					fontSize: "var(--app-topbar-title-size, 16px)",
					fontWeight: 600,
					color: "var(--app-text)",
					whiteSpace: "nowrap",
				}}
			>
				{pageTitle}
			</div>

			<button
				type="button"
				className="app-mobile-search-trigger"
				aria-label="Qidirishni ochish"
				onClick={() => setMobileSearchActive(true)}
				style={{
					display: "none",
					alignItems: "center",
					justifyContent: "center",
					width: 34,
					height: 34,
					borderRadius: 8,
					border: "1px solid var(--app-border)",
					background: "var(--app-surface)",
					color: "var(--app-text-soft)",
					cursor: "pointer",
				}}
			>
				<Search size={15} />
			</button>

			<div
				ref={searchRef}
				className={`app-topbar-search ${mobileSearchActive ? "app-topbar-search--active" : ""}`}
				style={{
					position: "relative",
					flex: "0 1 200px",
					width: "clamp(160px, 16vw, 200px)",
					minWidth: 140,
					display: "var(--app-topbar-search-display, block)",
				}}
			>
				<button
					type="button"
					className="app-mobile-search-close"
					aria-label="Qidirishni yopish"
					onClick={() => {
						setMobileSearchActive(false);
						setSearchTerm("");
					}}
					style={{
						display: "none",
						alignItems: "center",
						justifyContent: "center",
						width: 34,
						height: 34,
						borderRadius: 8,
						border: "1px solid var(--app-border)",
						background: "var(--app-surface)",
						color: "var(--app-text-soft)",
						cursor: "pointer",
						flexShrink: 0,
					}}
				>
					<ArrowLeft size={16} />
				</button>

				<div style={{ position: "relative", flex: 1 }}>
					<Search
						size={14}
						style={{
							position: "absolute",
							left: 12,
							top: "50%",
							transform: "translateY(-50%)",
							color: "var(--app-text-muted)",
							pointerEvents: "none",
						}}
					/>
					<input
						value={searchTerm}
						onChange={(e) => {
							setSearchTerm(e.target.value);
							setShowSearch(true);
						}}
						onFocus={() => setShowSearch(true)}
						placeholder="Qidirish..."
						style={{
							width: "100%",
							height: 34,
							borderRadius: 8,
							border: "1px solid var(--app-border)",
							background: "var(--app-surface)",
							color: "var(--app-text)",
							padding: "0 12px 0 34px",
							fontSize: 12,
							outline: "none",
							fontFamily: "'Inter', sans-serif",
						}}
					/>
					{showSearch && searchTerm.trim().length >= 2 && (
						<div
							style={{
								position: "absolute",
								top: "calc(100% + 8px)",
								left: 0,
								right: 0,
								background: "var(--app-surface)",
								border: "1px solid var(--app-border)",
								borderRadius: 10,
								boxShadow: "var(--app-shadow)",
								zIndex: 1000,
								overflow: "hidden",
							}}
						>
							{searchLoading ? (
								<div style={{ padding: "12px 14px", fontSize: 12, color: "var(--app-text-muted)" }}>
									Qidirilmoqda...
								</div>
							) : searchResults.length > 0 ? (
								searchResults.map((result) => (
									<button
										key={`${result.type}-${result.id}`}
										type="button"
										onClick={() => openSearchResult(result)}
										style={{
											width: "100%",
											padding: "10px 14px",
											border: "none",
											borderBottom: "1px solid var(--app-border)",
											background: "transparent",
											color: "var(--app-text)",
											textAlign: "left",
											cursor: "pointer",
											fontFamily: "'Inter', sans-serif",
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.background = "var(--app-surface-elevated)")
										}
										onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
									>
										<div style={{ fontSize: 13, fontWeight: 600 }}>{result.title || "-"}</div>
										<div style={{ fontSize: 11, color: "var(--app-text-muted)", marginTop: 2 }}>
											{result.subtitle || (result.type === "user" ? "Foydalanuvchi" : result.type)}
										</div>
									</button>
								))
							) : (
								<div style={{ padding: "12px 14px", fontSize: 12, color: "var(--app-text-muted)" }}>
									Natija topilmadi
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Spacer */}
			<div style={{ flex: 1 }} />

			<LiveClock />

			<div
				className="app-topbar-divider"
				style={{ width: 1, height: 20, background: "var(--app-border)" }}
			/>

			<button
				onClick={toggleTheme}
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 34,
					height: 34,
					borderRadius: 8,
					border: "1px solid var(--app-border)",
					background: "var(--app-surface)",
					color: "var(--app-text-soft)",
					cursor: "pointer",
				}}
				title={isDark ? "Light mode" : "Dark mode"}
			>
				{isDark ? <Sun size={15} /> : <Moon size={15} />}
			</button>

			{/* Topbar Export Button with Dropdown */}
			<div ref={exportRef} style={{ position: "relative" }}>
				<button
					type="button"
					onClick={() => {
						setShowExport((p) => !p);
						setShowNotif(false);
						setShowUser(false);
					}}
					disabled={!!exportingType}
					className="app-topbar-action"
					style={{
						display: "var(--app-topbar-action-display, flex)",
						alignItems: "center",
						gap: 6,
						height: 34,
						padding: "0 12px",
						borderRadius: 8,
						border: "1px solid var(--app-border)",
						background: showExport ? "var(--app-surface-elevated)" : "var(--app-surface)",
						color: exportingType ? "var(--app-text-muted)" : "var(--app-text-soft)",
						fontSize: 12,
						fontWeight: 500,
						cursor: exportingType ? "wait" : "pointer",
						fontFamily: "'Inter', sans-serif",
						whiteSpace: "nowrap",
					}}
					title="Ma'lumotlarni Excel formatida eksport qilish"
				>
					<Download size={13} className={exportingType ? "animate-spin" : ""} />
					<span>{exportingType ? "Eksport..." : "Export"}</span>
					<ChevronDown size={11} />
				</button>

				{showExport && (
					<div
						style={{
							position: "absolute",
							top: "calc(100% + 8px)",
							right: 0,
							width: 220,
							background: "var(--app-surface)",
							border: "1px solid var(--app-border)",
							borderRadius: 10,
							boxShadow: "var(--app-shadow)",
							zIndex: 1000,
							padding: "6px 0",
						}}
					>
						<div
							style={{
								padding: "6px 14px 4px",
								fontSize: 11,
								fontWeight: 600,
								color: "var(--app-text-muted)",
								textTransform: "uppercase",
								letterSpacing: "0.05em",
							}}
						>
							Excel Hisobotlar
						</div>
						<button
							type="button"
							onClick={() => handleTopExport("today_all")}
							style={{
								width: "100%",
								padding: "8px 14px",
								display: "flex",
								alignItems: "center",
								gap: 8,
								background: "transparent",
								border: "none",
								color: "var(--app-text)",
								fontSize: 12,
								textAlign: "left",
								cursor: "pointer",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.background = "var(--app-surface-elevated)")
							}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<Download size={12} color="#10B981" />
							Bugungi umumiy davomat
						</button>
						<button
							type="button"
							onClick={() => handleTopExport("today_employee")}
							style={{
								width: "100%",
								padding: "8px 14px",
								display: "flex",
								alignItems: "center",
								gap: 8,
								background: "transparent",
								border: "none",
								color: "var(--app-text)",
								fontSize: 12,
								textAlign: "left",
								cursor: "pointer",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.background = "var(--app-surface-elevated)")
							}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<Download size={12} color="#3B82F6" />
							Xodimlar davomati
						</button>
						<div style={{ height: 1, background: "var(--app-border)", margin: "4px 0" }} />
						<div
							style={{
								padding: "6px 14px 4px",
								fontSize: 11,
								fontWeight: 600,
								color: "var(--app-text-muted)",
								textTransform: "uppercase",
								letterSpacing: "0.05em",
							}}
						>
							Ro'yxatlar
						</div>
						<button
							type="button"
							onClick={() => handleTopExport("students")}
							style={{
								width: "100%",
								padding: "8px 14px",
								display: "flex",
								alignItems: "center",
								gap: 8,
								background: "transparent",
								border: "none",
								color: "var(--app-text)",
								fontSize: 12,
								textAlign: "left",
								cursor: "pointer",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.background = "var(--app-surface-elevated)")
							}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<Download size={12} color="#8B5CF6" />
							Talabalar ro'yxati
						</button>
						<button
							type="button"
							onClick={() => handleTopExport("teachers")}
							style={{
								width: "100%",
								padding: "8px 14px",
								display: "flex",
								alignItems: "center",
								gap: 8,
								background: "transparent",
								border: "none",
								color: "var(--app-text)",
								fontSize: 12,
								textAlign: "left",
								cursor: "pointer",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.background = "var(--app-surface-elevated)")
							}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<Download size={12} color="#F59E0B" />
							O'qituvchilar ro'yxati
						</button>
						<button
							type="button"
							onClick={() => handleTopExport("employees")}
							style={{
								width: "100%",
								padding: "8px 14px",
								display: "flex",
								alignItems: "center",
								gap: 8,
								background: "transparent",
								border: "none",
								color: "var(--app-text)",
								fontSize: 12,
								textAlign: "left",
								cursor: "pointer",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.background = "var(--app-surface-elevated)")
							}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<Download size={12} color="#06B6D4" />
							Xodimlar ro'yxati
						</button>
					</div>
				)}
			</div>

			<div
				className="app-topbar-divider"
				style={{ width: 1, height: 20, background: "var(--app-border)" }}
			/>

			{/* Notifications */}
			<div ref={notifRef} style={{ position: "relative" }}>
				<button
					onClick={() => {
						setShowNotif((p) => !p);
						setShowUser(false);
					}}
					style={{
						width: 34,
						height: 34,
						borderRadius: 8,
						border: "1px solid var(--app-border)",
						background: "var(--app-surface)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
						position: "relative",
						color: "var(--app-text-soft)",
					}}
				>
					<Bell size={16} />
					{unread > 0 && (
						<span
							style={{
								position: "absolute",
								top: -4,
								right: -4,
								background: "#EF4444",
								color: "#FFFFFF",
								fontSize: 9,
								fontWeight: 700,
								minWidth: 16,
								height: 16,
								borderRadius: 9999,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								padding: "0 3px",
							}}
						>
							{unread}
						</span>
					)}
				</button>

				{showNotif && (
					<div
						className="app-notification-panel"
						style={{
							position: "absolute",
							right: 0,
							top: "calc(100% + 8px)",
							width: 360,
							background: "var(--app-surface)",
							border: "1px solid var(--app-border)",
							borderRadius: 12,
							boxShadow: "var(--app-shadow)",
							zIndex: 1000,
							overflow: "hidden",
							animation: "dropIn 150ms",
						}}
					>
						<style>{`@keyframes dropIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
						<div
							style={{
								padding: "14px 16px",
								borderBottom: "1px solid var(--app-border)",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<span style={{ fontSize: 14, fontWeight: 600, color: "var(--app-text)" }}>
								Bildirishnomalar
							</span>
							<button
								onClick={markAllRead}
								style={{
									fontSize: 12,
									color: "#00C98D",
									background: "none",
									border: "none",
									cursor: "pointer",
									fontFamily: "'Inter', sans-serif",
								}}
							>
								Hammasini o'qi
							</button>
						</div>
						{notifList.map((n) => (
							<div
								key={n.id}
								style={{
									padding: "12px 16px",
									borderBottom: "1px solid var(--app-border)",
									background: n.read ? "transparent" : "rgba(0,201,141,0.04)",
									display: "flex",
									gap: 10,
								}}
							>
								<span style={{ fontSize: 16, flexShrink: 0 }}>{notifIcon[n.type]}</span>
								<div>
									<div style={{ fontSize: 13, color: "var(--app-text)", marginBottom: 2 }}>
										{n.text}
									</div>
									<div style={{ fontSize: 11, color: "var(--app-text-muted)" }}>{n.time}</div>
								</div>
								{!n.read && (
									<div
										style={{
											width: 6,
											height: 6,
											borderRadius: "50%",
											background: "#00C98D",
											marginLeft: "auto",
											flexShrink: 0,
											marginTop: 4,
										}}
									/>
								)}
							</div>
						))}
					</div>
				)}
			</div>

			{/* User Menu */}
			<div ref={userRef} style={{ position: "relative" }}>
				<button
					onClick={() => {
						setShowUser((p) => !p);
						setShowNotif(false);
					}}
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						height: 34,
						padding: "0 10px",
						borderRadius: 8,
						border: "1px solid var(--app-border)",
						background: "var(--app-surface)",
						cursor: "pointer",
						color: "var(--app-text-soft)",
					}}
				>
					<div
						className="app-user-avatar"
						style={{
							width: "var(--app-user-avatar-size, 24px)",
							height: "var(--app-user-avatar-size, 24px)",
							borderRadius: "50%",
							background: "linear-gradient(135deg,#00C98D,#0EA5E9)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 10,
							fontWeight: 700,
							color: "#FFFFFF",
						}}
					>
						{user?.fullName
							? user.fullName
									.split(" ")
									.map((n: string) => n[0])
									.join("")
									.toUpperCase()
									.slice(0, 2)
							: "AD"}
					</div>
					<span
						className="app-user-name"
						style={{ fontSize: 12, color: "var(--app-text)", fontFamily: "'Inter', sans-serif" }}
					>
						{user?.fullName || "Admin"}
					</span>
					<ChevronDown size={12} />
				</button>

				{showUser && (
					<div
						style={{
							position: "absolute",
							right: 0,
							top: "calc(100% + 8px)",
							width: 200,
							background: "var(--app-surface)",
							border: "1px solid var(--app-border)",
							borderRadius: 10,
							boxShadow: "var(--app-shadow)",
							zIndex: 1000,
							overflow: "hidden",
							animation: "dropIn 150ms",
						}}
					>
						<div style={{ padding: "12px 16px", borderBottom: "1px solid var(--app-border)" }}>
							<div style={{ fontSize: 13, fontWeight: 600, color: "var(--app-text)" }}>
								{user?.fullName || "Foydalanuvchi"}
							</div>
							<div style={{ fontSize: 11, color: "var(--app-text-muted)" }}>
								{user?.role === "super_admin"
									? "Super Admin"
									: user?.role === "hr_manager"
										? "HR Manager"
										: user?.role === "operator"
											? "Operator"
											: user?.role === "security"
												? "Xavfsizlik xodimi"
												: user?.role || "Foydalanuvchi"}
							</div>
						</div>
						{[
							{ icon: User, label: "Profil" },
							{ icon: KeyRound, label: "Parol o'zgartirish" },
						].map((item) => (
							<button
								key={item.label}
								type="button"
								onClick={() => {
									navigate("/settings");
									setShowUser(false);
								}}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									gap: 10,
									padding: "10px 16px",
									background: "none",
									border: "none",
									color: "var(--app-text-soft)",
									fontSize: 13,
									cursor: "pointer",
									fontFamily: "'Inter', sans-serif",
									textAlign: "left",
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.background = "var(--app-surface)";
									e.currentTarget.style.color = "var(--app-text)";
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.background = "transparent";
									e.currentTarget.style.color = "var(--app-text-soft)";
								}}
							>
								<item.icon size={14} /> {item.label}
							</button>
						))}
						<div style={{ borderTop: "1px solid var(--app-border)" }}>
							<button
								type="button"
								onClick={() => {
									logout();
									setShowUser(false);
								}}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									gap: 10,
									padding: "10px 16px",
									background: "none",
									border: "none",
									color: "#F87171",
									fontSize: 13,
									cursor: "pointer",
									fontFamily: "'Inter', sans-serif",
									textAlign: "left",
								}}
								onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.08)")}
								onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
							>
								<LogOut size={14} /> Chiqish
							</button>
						</div>
					</div>
				)}
			</div>
		</header>
	);
}
